import { defineMutation, useMutation, useQueryCache } from '@pinia/colada';
import { AxiosError } from 'axios';

import { useAlertsStore } from '@tallo/design-system';

import {
  AddPropertyShowingAgentDto,
  TeamShowingAgentsDto,
  TeamSubject,
} from '../interfaces/property-showing-agent.dto';
import { getPropertyShowingAgentsQueryKey } from '../queries/property-showing-agents.query';
import { showingAgentsQueryKey } from '../queries/showing-agents.query';
import { showingAgentsServiceFor } from '../services/showing-agents.service';

/**
 * The unit or building answers with its new list, shown as is. A unit's add lands on every unit of the
 * portfolio, and any add may create a roster entry, so the roster and the other lists refresh too; the
 * change itself already happened, so a failed refresh is not an error.
 */
function showNewList(list: TeamShowingAgentsDto, subject: TeamSubject) {
  const queryCache = useQueryCache();

  queryCache.setQueryData(getPropertyShowingAgentsQueryKey(subject), list);
  queryCache.invalidateQueries({ key: [showingAgentsQueryKey] }).catch(() => undefined);
}

export const useAddPropertyShowingAgent = defineMutation(() => {
  return useMutation({
    mutation(payload: { subject: TeamSubject; agent: AddPropertyShowingAgentDto }) {
      return showingAgentsServiceFor(payload.subject).addToProperty(payload.subject.id, payload.agent);
    },
    onSuccess: (list, payload) => showNewList(list, payload.subject),
    onError(error) {
      // A rejected field of a new contact is shown on the form that sent it
      if (error instanceof AxiosError && error.response?.data?.validationErrors) {
        return;
      }

      useAlertsStore().catchError(error);
    },
  });
});

export const useRemovePropertyShowingAgent = defineMutation(() => {
  return useMutation({
    mutation(payload: { subject: TeamSubject; showingAgentId: string }) {
      return showingAgentsServiceFor(payload.subject).removeFromProperty(payload.subject.id, payload.showingAgentId);
    },
    onSuccess: (list, payload) => showNewList(list, payload.subject),
    onError(error) {
      useAlertsStore().catchError(error);
    },
  });
});
